/**
 * DeviceRenderer - Renders device DOM elements from device/template data
 * 
 * Pure render logic - takes device and template data, returns HTML/DOM elements.
 * Event binding and state management handled externally.
 */

import { escapeHtml } from '../utils/index.js';
import {
    CONNECTOR_TYPES
} from '../constants/index.js';
import {
    getConnectorIcon,
    getConnectorShortCode,
    normalizeSignalTypes
} from '../services/ConnectionService.js';

/**
 * Get display name for a port group
 * Uses name if defined, falls back to type with first letter uppercase
 */
export function getGroupDisplayName(group) {
    if (group.name) return group.name;
    // Inline type normalization to avoid dependency on normalizeSignalTypes (defined later)
    const types = group.type ? (Array.isArray(group.type) ? group.type : group.type.split(',').map(t => t.trim())) : ['audio'];
    const primaryType = types[0] || 'audio';
    return primaryType.charAt(0).toUpperCase() + primaryType.slice(1);
}

/**
 * Expand a group if it has splitSize or connector-based subgrouping
 * @param {Object} group - Port group definition
 * @param {Object|string} layoutOverride - Instance-specific layout override
 * @param {number} groupIndex - Index of the group in template
 * @returns {Array} Array of (possibly split) groups with foldKey attached
 */
export function expandGroupForRendering(group, layoutOverride, groupIndex) {
    const override = layoutOverride || {};
    // Explicit splitSize from instance layout takes precedence
    let splitSize = (typeof override === 'object') ? override.splitSize : null;
    
    // If no explicit splitSize, check if connector type has subgroupSize (e.g., D-sub = 8)
    if (!splitSize && group.connector) {
        const connectorInfo = CONNECTOR_TYPES[group.connector];
        if (connectorInfo && connectorInfo.subgroupSize) {
            splitSize = connectorInfo.subgroupSize;
        }
    }
    
    const count = parseInt(group.count) || 1;
    const offset = group.offset ? parseInt(group.offset) : 1;
    const labels = group.labels ? group.labels.split(',').map(l => l.trim()) : null;
    
    // If no split or count <= splitSize, return single group
    // Use group.id if available, otherwise fall back to numeric index string
    if (!splitSize || count <= splitSize) {
        return [{ ...group, foldKey: group.id || String(groupIndex) }];
    }
    
    // Split into multiple sub-groups for visual rendering
    const subGroups = [];
    let remaining = count;
    let currentOffset = offset;
    
    while (remaining > 0) {
        const subCount = Math.min(remaining, splitSize);
        const endChannel = currentOffset + subCount - 1;
        
        // Generate sub-group name with range
        const subName = `${getGroupDisplayName(group)} ${currentOffset}-${endChannel}`;
        
        // Extract labels for this sub-group if provided
        let subLabels = undefined;
        if (labels && labels.length > 0) {
            const startIdx = currentOffset - offset;
            const subLabelArr = labels.slice(startIdx, startIdx + subCount);
            if (subLabelArr.length > 0) {
                subLabels = subLabelArr.join(',');
            }
        }
        
        const groupKey = group.id || String(groupIndex);
        subGroups.push({
            id: group.id, // Keep same group ID for connections
            name: subName,
            direction: group.direction,
            type: group.type,
            connector: group.connector, // Inherit connector type from parent
            count: subCount,
            offset: currentOffset,
            labels: subLabels,
            stereoPairs: group.stereoPairs,
            _parentGroupId: group.id,
            _subGroupStartIndex: currentOffset - offset + 1, // 1-based index into original group
            foldKey: `${groupKey}:${currentOffset}` // Unique key for fold state
        });
        
        currentOffset += subCount;
        remaining -= subCount;
    }
    
    return subGroups;
}

/**
 * Create inline SVG connector shape
 * @param {string} side - 'left', 'right', 'top', 'bottom'
 * @param {string} direction - 'input', 'output', 'insert'
 * @returns {string} SVG markup
 */
export function createTriangleSvg(side, direction) {
    // Insert ports use a diamond shape (bidirectional indicator)
    if (direction === 'insert') {
        if (side === 'top' || side === 'bottom') {
            return `<svg viewBox="0 0 12 10" xmlns="http://www.w3.org/2000/svg"><polygon points="6,1 11,5 6,9 1,5"/></svg>`;
        }
        return `<svg viewBox="0 0 10 12" xmlns="http://www.w3.org/2000/svg"><polygon points="5,1 9,6 5,11 1,6"/></svg>`;
    }
    
    const isInput = direction === 'input';
    // Input connectors point INTO the device, output connectors point OUT
    let points;
    if (side === 'left') {
        // Left side: inputs point right, outputs point left
        points = isInput ? "1,1 9,6 1,11" : "9,1 1,6 9,11";
    } else if (side === 'right') {
        // Right side: inputs point left, outputs point right
        points = isInput ? "9,1 1,6 9,11" : "1,1 9,6 1,11";
    } else if (side === 'top') {
        // Top side: inputs point down, outputs point up
        points = isInput ? "1,1 6,9 11,1" : "1,9 6,1 11,9";
        return `<svg viewBox="0 0 12 10" xmlns="http://www.w3.org/2000/svg"><polygon points="${points}"/></svg>`;
    } else if (side === 'bottom') {
        // Bottom side: inputs point up, outputs point down
        points = isInput ? "1,9 6,1 11,9" : "1,1 6,9 11,1";
        return `<svg viewBox="0 0 12 10" xmlns="http://www.w3.org/2000/svg"><polygon points="${points}"/></svg>`;
    } else {
        points = "1,1 9,6 1,11"; // Default
    }
    return `<svg viewBox="0 0 10 12" xmlns="http://www.w3.org/2000/svg"><polygon points="${points}"/></svg>`;
}

/**
 * Create SVG connector with optional inline style
 * @param {string} side - 'left', 'right', 'top', 'bottom'
 * @param {string} direction - 'input', 'output', 'insert'
 * @param {string} polygonStyle - Optional inline style for the polygon
 * @returns {string} SVG markup
 */
export function createTriangleSvgWithStyle(side, direction, polygonStyle = '') {
    // Insert ports use a diamond shape (bidirectional indicator)
    if (direction === 'insert') {
        if (side === 'top' || side === 'bottom') {
            return `<svg viewBox="0 0 12 10" xmlns="http://www.w3.org/2000/svg"><polygon points="6,1 11,5 6,9 1,5" ${polygonStyle}/></svg>`;
        }
        return `<svg viewBox="0 0 10 12" xmlns="http://www.w3.org/2000/svg"><polygon points="5,1 9,6 5,11 1,6" ${polygonStyle}/></svg>`;
    }
    
    const isInput = direction === 'input';
    let points;
    if (side === 'left') {
        points = isInput ? "1,1 9,6 1,11" : "9,1 1,6 9,11";
    } else if (side === 'right') {
        points = isInput ? "9,1 1,6 9,11" : "1,1 9,6 1,11";
    } else if (side === 'top') {
        points = isInput ? "1,1 6,9 11,1" : "1,9 6,1 11,9";
        return `<svg viewBox="0 0 12 10" xmlns="http://www.w3.org/2000/svg"><polygon points="${points}" ${polygonStyle}/></svg>`;
    } else if (side === 'bottom') {
        points = isInput ? "1,9 6,1 11,9" : "1,1 6,9 11,1";
        return `<svg viewBox="0 0 12 10" xmlns="http://www.w3.org/2000/svg"><polygon points="${points}" ${polygonStyle}/></svg>`;
    } else {
        points = "1,1 9,6 1,11";
    }
    return `<svg viewBox="0 0 10 12" xmlns="http://www.w3.org/2000/svg"><polygon points="${points}" ${polygonStyle}/></svg>`;
}

/**
 * Get port label based on index, offset, custom labels, and stereo pairs
 * @param {Object} group - Port group definition
 * @param {number} i - 1-based port index within the group
 * @returns {string} Port label (escaped HTML)
 */
export function getPortLabel(group, i) {
    // If custom labels are defined, use them
    if (group.labels) {
        const labelList = group.labels.split(',').map(l => l.trim());
        if (i <= labelList.length && labelList[i - 1]) {
            return escapeHtml(labelList[i - 1]);
        }
    }
    // Otherwise use offset + index
    const offset = group.offset || 1;
    const portNum = offset + i - 1;
    
    // If stereo pairs enabled, format as NL/NR
    if (group.stereoPairs) {
        const pairIndex = Math.floor((i - 1) / 2); // 0-based pair index
        const isLeft = (i - 1) % 2 === 0;
        const pairNum = offset + pairIndex;
        return pairNum + (isLeft ? 'L' : 'R');
    }
    
    return String(portNum);
}

/**
 * Get actual port index (for split sub-groups, this is the index in the original group)
 * @param {Object} group - Port group (possibly a sub-group)
 * @param {number} localI - Local 1-based index within the sub-group
 * @returns {number} Actual port index
 */
export function getPortIndex(group, localI) {
    // If this is a split sub-group, calculate actual port index
    if (group._subGroupStartIndex) {
        return group._subGroupStartIndex + localI - 1;
    }
    return localI;
}

/**
 * Check if a group is folded (uses foldKey for subgroup-specific state)
 * @param {Object} group - Port group with foldKey
 * @param {Object} groupLayout - Device's groupLayout object
 * @returns {boolean} True if folded
 */
export function isGroupFolded(group, groupLayout) {
    // Use foldKey (set by expandGroupForRendering), or fall back to id, or _groupIndex
    const key = group.foldKey || group.id || String(group._groupIndex);
    const override = groupLayout?.[key];
    return (typeof override === 'object') && override.folded;
}

/**
 * Get custom color for a group
 * @param {Object} group - Port group with foldKey
 * @param {Object} groupLayout - Device's groupLayout object
 * @returns {string|null} Color string or null for default
 */
export function getGroupColor(group, groupLayout) {
    // Use foldKey (set by expandGroupForRendering), or fall back to id, or _groupIndex
    const key = group.foldKey || group.id || String(group._groupIndex);
    const override = groupLayout?.[key];
    if (typeof override === 'object' && override.color) {
        return override.color;
    }
    return null; // Use default type color
}

/**
 * Render a folded group
 * @param {Object} group - Port group definition
 * @param {string} side - 'left', 'right', 'top', 'bottom'
 * @param {string} deviceId - Device instance ID
 * @param {Object} groupLayout - Device's groupLayout object
 * @param {Function} groupHasConnections - Function to check if group has connections
 * @returns {string} HTML string
 */
export function renderFoldedGroup(group, side, deviceId, groupLayout, groupHasConnections) {
    const groupIndex = group._groupIndex;
    const customColor = getGroupColor(group, groupLayout);
    const connectorStyle = customColor ? `style="color: ${customColor};"` : '';
    const polygonStyle = customColor 
        ? `style="fill: ${customColor}; stroke: ${customColor};"` 
        : '';
    const hasConn = groupHasConnections(deviceId, groupIndex, group.direction);
    const connectedClass = hasConn ? 'connected' : '';
    const foldKey = group.foldKey || String(groupIndex);
    const groupTypes = normalizeSignalTypes(group.type);
    const primaryType = groupTypes[0];
    const connectorIcon = group.connector ? getConnectorIcon(group.connector) : '';
    const connectorAttr = group.connector ? `data-connector="${group.connector}" data-connector-label="${getConnectorShortCode(group.connector)}"` : '';
    const typeAttr = groupTypes.length > 1 ? `data-types="${groupTypes.join(',')}"` : '';
    
    // Check if this is a multi-channel connector (DB25, DB9, etc.)
    const connectorInfo = group.connector ? CONNECTOR_TYPES[group.connector] : null;
    const isMultiChannel = connectorInfo && connectorInfo.subgroupSize;
    const channelBadge = isMultiChannel 
        ? `<span class="folded-channel-badge" title="${connectorInfo.label} (${connectorInfo.subgroupSize} channels per connector)">${group.count}ch</span>` 
        : '';
    
    // Connector is positioned at the edge (via CSS), separate from fold toggle
    // The connector is the cable connection point
    // The fold-toggle is the clickable button to unfold
    return `<div class="port-group-folded ${primaryType} ${connectedClass}" 
                 data-device="${deviceId}" 
                 data-group="${groupIndex}" 
                 data-fold-key="${foldKey}"
                 data-type="${primaryType}" 
                 data-direction="${group.direction}"
                 data-folded="true"
                 data-side="${side}"
                 ${connectorAttr}
                 ${typeAttr}>
        <span class="folded-connector" ${connectorStyle}>${createTriangleSvgWithStyle(side, group.direction, polygonStyle)}</span>
        <span class="folded-content" onclick="unfoldGroup('${deviceId}', '${foldKey}')" title="Click to unfold">
            <span class="folded-label">${connectorIcon}${escapeHtml(getGroupDisplayName(group))}${channelBadge}</span>
            <span class="folded-count">[${group.count}]</span>
            <span class="fold-toggle">▸</span>
        </span>
    </div>`;
}

/**
 * Render ports for the left side of a device
 * @param {Array} groups - Array of port groups to render
 * @param {string} deviceId - Device instance ID
 * @param {Object} groupLayout - Device's groupLayout object
 * @param {Function} groupHasConnections - Function to check if group has connections
 * @param {string} portOrder - 'default' or 'clockwise' (clockwise reverses to bottom-to-top)
 * @returns {string} HTML string
 */
export function renderLeftGroups(groups, deviceId, groupLayout, groupHasConnections, portOrder = 'default') {
    let html = '';
    
    // In clockwise mode, reverse the groups order (bottom to top)
    const orderedGroups = portOrder === 'clockwise' ? [...groups].reverse() : groups;
    
    orderedGroups.forEach(group => {
        const groupIndex = group._groupIndex;
        const customColor = getGroupColor(group, groupLayout);
        const labelStyle = customColor ? `style="color: ${customColor};"` : '';
        
        if (isGroupFolded(group, groupLayout)) {
            html += renderFoldedGroup(group, 'left', deviceId, groupLayout, groupHasConnections);
        } else {
            const foldKey = group.foldKey || String(groupIndex);
            const connectorIcon = group.connector ? getConnectorIcon(group.connector) : '';
            const groupTypes = normalizeSignalTypes(group.type);
            const primaryType = groupTypes[0];
            
            // Check if this is a multi-channel subgroup (from a D-sub or similar connector)
            const connectorInfo = group.connector ? CONNECTOR_TYPES[group.connector] : null;
            const isSubgroup = group._parentGroupId || (connectorInfo && connectorInfo.subgroupSize);
            const subgroupClass = isSubgroup ? `port-group-subgroup side-left ${primaryType}` : '';
            
            if (isSubgroup) html += `<div class="${subgroupClass}">`;
            
            html += `<div class="port-group-header" data-device="${deviceId}" data-group="${groupIndex}" data-fold-key="${foldKey}">
                <span class="port-group-label ${primaryType}" ${labelStyle}>${connectorIcon}${escapeHtml(getGroupDisplayName(group))}</span>
                <button class="fold-toggle-inline" onclick="foldGroupInline('${deviceId}', '${foldKey}', event)" title="Fold group">◀</button>
            </div>`;
            // Use direction for connector shape (input/output/insert)
            // Insert ports use 'insert' type styling added to port-connector class
            const connectorClass = group.direction === 'insert' ? `${primaryType} insert` : primaryType;
            const connectorAttr = group.connector ? `data-connector="${group.connector}" data-connector-label="${getConnectorShortCode(group.connector)}"` : '';
            const typeAttr = groupTypes.length > 1 ? `data-types="${groupTypes.join(',')}"` : '';
            
            for (let i = 1; i <= group.count; i++) {
                const label = getPortLabel(group, i);
                const portIndex = getPortIndex(group, i);
                const polygonStyle = customColor 
                    ? `style="fill: transparent; stroke: ${customColor};"` 
                    : '';
                html += `<div class="port port-input" data-device="${deviceId}" data-group="${groupIndex}" data-type="${primaryType}" data-direction="${group.direction}" data-index="${portIndex}" data-side="left" ${connectorAttr} ${typeAttr} ${customColor ? `data-color="${customColor}"` : ''}>
                    <span class="port-connector ${connectorClass}">${createTriangleSvgWithStyle('left', group.direction, polygonStyle)}</span>
                    <span>${label}</span>
                </div>`;
            }
            
            if (isSubgroup) html += `</div>`;
        }
    });
    
    return html;
}

/**
 * Render ports for the right side of a device
 * @param {Array} groups - Array of port groups to render
 * @param {string} deviceId - Device instance ID
 * @param {Object} groupLayout - Device's groupLayout object
 * @param {Function} groupHasConnections - Function to check if group has connections
 * @returns {string} HTML string
 */
export function renderRightGroups(groups, deviceId, groupLayout, groupHasConnections) {
    let html = '';
    
    groups.forEach(group => {
        const groupIndex = group._groupIndex;
        const customColor = getGroupColor(group, groupLayout);
        const labelStyle = customColor ? `style="color: ${customColor};"` : '';
        
        if (isGroupFolded(group, groupLayout)) {
            html += renderFoldedGroup(group, 'right', deviceId, groupLayout, groupHasConnections);
        } else {
            const foldKey = group.foldKey || String(groupIndex);
            const connectorIcon = group.connector ? getConnectorIcon(group.connector) : '';
            const groupTypes = normalizeSignalTypes(group.type);
            const primaryType = groupTypes[0];
            
            // Check if this is a multi-channel subgroup (from a D-sub or similar connector)
            const connectorInfo = group.connector ? CONNECTOR_TYPES[group.connector] : null;
            const isSubgroup = group._parentGroupId || (connectorInfo && connectorInfo.subgroupSize);
            const subgroupClass = isSubgroup ? `port-group-subgroup side-right ${primaryType}` : '';
            
            if (isSubgroup) html += `<div class="${subgroupClass}">`;
            
            html += `<div class="port-group-header" data-device="${deviceId}" data-group="${groupIndex}" data-fold-key="${foldKey}">
                <button class="fold-toggle-inline" onclick="foldGroupInline('${deviceId}', '${foldKey}', event)" title="Fold group">▶</button>
                <span class="port-group-label ${primaryType}" ${labelStyle}>${escapeHtml(getGroupDisplayName(group))}${connectorIcon}</span>
            </div>`;
            // Use direction for connector shape (input/output/insert)
            const connectorClass = group.direction === 'insert' ? `${primaryType} insert` : primaryType;
            const connectorAttr = group.connector ? `data-connector="${group.connector}" data-connector-label="${getConnectorShortCode(group.connector)}"` : '';
            const typeAttr = groupTypes.length > 1 ? `data-types="${groupTypes.join(',')}"` : '';
            for (let i = 1; i <= group.count; i++) {
                const label = getPortLabel(group, i);
                const portIndex = getPortIndex(group, i);
                const polygonStyle = customColor 
                    ? `style="fill: transparent; stroke: ${customColor};"` 
                    : '';
                html += `<div class="port port-output" data-device="${deviceId}" data-group="${groupIndex}" data-type="${primaryType}" data-direction="${group.direction}" data-index="${portIndex}" data-side="right" ${connectorAttr} ${typeAttr} ${customColor ? `data-color="${customColor}"` : ''}>
                    <span>${label}</span>
                    <span class="port-connector ${connectorClass}">${createTriangleSvgWithStyle('right', group.direction, polygonStyle)}</span>
                </div>`;
            }
            
            if (isSubgroup) html += `</div>`;
        }
    });
    
    return html;
}

/**
 * Render ports for the top side of a device
 * @param {Array} groups - Array of port groups to render
 * @param {string} deviceId - Device instance ID
 * @param {Object} groupLayout - Device's groupLayout object
 * @param {Function} groupHasConnections - Function to check if group has connections
 * @returns {string} HTML string
 */
export function renderTopGroups(groups, deviceId, groupLayout, groupHasConnections) {
    let html = '';
    
    groups.forEach((group, groupIdx) => {
        const groupIndex = group._groupIndex;
        const customColor = getGroupColor(group, groupLayout);
        const labelStyle = customColor ? `style="color: ${customColor};"` : '';
        
        if (isGroupFolded(group, groupLayout)) {
            if (groupIdx > 0) html += '<div class="port-group-divider"></div>';
            html += renderFoldedGroup(group, 'top', deviceId, groupLayout, groupHasConnections);
        } else {
            if (groupIdx > 0) html += '<div class="port-group-divider"></div>';
            const foldKey = group.foldKey || String(groupIndex);
            const connectorIcon = group.connector ? getConnectorIcon(group.connector) : '';
            const groupTypes = normalizeSignalTypes(group.type);
            const primaryType = groupTypes[0];
            
            // Check if this is a multi-channel subgroup
            const connectorInfo = group.connector ? CONNECTOR_TYPES[group.connector] : null;
            const isSubgroup = group._parentGroupId || (connectorInfo && connectorInfo.subgroupSize);
            const subgroupClass = isSubgroup ? ` port-group-subgroup side-top ${primaryType}` : '';
            
            html += `<div class="port-group${subgroupClass}">`;
            // Row 1: Ports (connector at top/edge, then number below)
            html += `<div class="ports-row">`;
            // Use direction for connector shape (input/output/insert)
            const connectorClass = group.direction === 'insert' ? `${primaryType} insert` : primaryType;
            const connectorAttr = group.connector ? `data-connector="${group.connector}" data-connector-label="${getConnectorShortCode(group.connector)}"` : '';
            const typeAttr = groupTypes.length > 1 ? `data-types="${groupTypes.join(',')}"` : '';
            for (let i = 1; i <= group.count; i++) {
                const label = getPortLabel(group, i);
                const portIndex = getPortIndex(group, i);
                const polygonStyle = customColor 
                    ? `style="fill: transparent; stroke: ${customColor};"` 
                    : '';
                html += `<div class="port port-top" data-device="${deviceId}" data-group="${groupIndex}" data-type="${primaryType}" data-direction="${group.direction}" data-index="${portIndex}" data-side="top" ${connectorAttr} ${typeAttr} ${customColor ? `data-color="${customColor}"` : ''}>
                    <span class="port-connector ${connectorClass}">${createTriangleSvgWithStyle('top', group.direction, polygonStyle)}</span>
                    <span class="port-number">${label}</span>
                </div>`;
            }
            html += `</div>`;
            // Row 2: Group label with fold toggle (furthest from edge, toward center)
            html += `<div class="port-group-header" data-device="${deviceId}" data-group="${groupIndex}" data-fold-key="${foldKey}" style="justify-content: center;">
                <span class="port-group-label ${primaryType}" ${labelStyle}>${connectorIcon}${escapeHtml(getGroupDisplayName(group))}</span>
                <button class="fold-toggle-inline" onclick="foldGroupInline('${deviceId}', '${foldKey}', event)" title="Fold group">▼</button>
            </div>`;
            html += `</div>`;
        }
    });
    
    return html;
}

/**
 * Render ports for the bottom side of a device
 * @param {Array} groups - Array of port groups to render
 * @param {string} deviceId - Device instance ID
 * @param {Object} groupLayout - Device's groupLayout object
 * @param {Function} groupHasConnections - Function to check if group has connections
 * @param {string} portOrder - 'default' or 'clockwise' (clockwise reverses to right-to-left)
 * @returns {string} HTML string
 */
export function renderBottomGroups(groups, deviceId, groupLayout, groupHasConnections, portOrder = 'default') {
    let html = '';
    
    // In clockwise mode, reverse the groups order (right to left)
    const orderedGroups = portOrder === 'clockwise' ? [...groups].reverse() : groups;
    
    orderedGroups.forEach((group, groupIdx) => {
        const groupIndex = group._groupIndex;
        const customColor = getGroupColor(group, groupLayout);
        const labelStyle = customColor ? `style="color: ${customColor};"` : '';
        
        if (isGroupFolded(group, groupLayout)) {
            if (groupIdx > 0) html += '<div class="port-group-divider"></div>';
            html += renderFoldedGroup(group, 'bottom', deviceId, groupLayout, groupHasConnections);
        } else {
            if (groupIdx > 0) html += '<div class="port-group-divider"></div>';
            const foldKey = group.foldKey || String(groupIndex);
            const connectorIcon = group.connector ? getConnectorIcon(group.connector) : '';
            const groupTypes = normalizeSignalTypes(group.type);
            const primaryType = groupTypes[0];
            
            // Check if this is a multi-channel subgroup
            const connectorInfo = group.connector ? CONNECTOR_TYPES[group.connector] : null;
            const isSubgroup = group._parentGroupId || (connectorInfo && connectorInfo.subgroupSize);
            const subgroupClass = isSubgroup ? ` port-group-subgroup side-bottom ${primaryType}` : '';
            
            html += `<div class="port-group${subgroupClass}">`;
            // Row 1: Group label with fold toggle (closest to center)
            html += `<div class="port-group-header" data-device="${deviceId}" data-group="${groupIndex}" data-fold-key="${foldKey}" style="justify-content: center;">
                <span class="port-group-label ${primaryType}" ${labelStyle}>${connectorIcon}${escapeHtml(getGroupDisplayName(group))}</span>
                <button class="fold-toggle-inline" onclick="foldGroupInline('${deviceId}', '${foldKey}', event)" title="Fold group">▲</button>
            </div>`;
            // Row 2: Ports (number first, then connector at bottom/edge)
            html += `<div class="ports-row">`;
            // Use direction for connector shape (input/output/insert)
            const connectorClassBottom = group.direction === 'insert' ? `${primaryType} insert` : primaryType;
            const connectorAttr = group.connector ? `data-connector="${group.connector}" data-connector-label="${getConnectorShortCode(group.connector)}"` : '';
            const typeAttr = groupTypes.length > 1 ? `data-types="${groupTypes.join(',')}"` : '';
            
            for (let i = 1; i <= group.count; i++) {
                const label = getPortLabel(group, i);
                const portIndex = getPortIndex(group, i);
                const polygonStyle = customColor 
                    ? `style="fill: transparent; stroke: ${customColor};"` 
                    : '';
                html += `<div class="port port-bottom" data-device="${deviceId}" data-group="${groupIndex}" data-type="${primaryType}" data-direction="${group.direction}" data-index="${portIndex}" data-side="bottom" ${connectorAttr} ${typeAttr} ${customColor ? `data-color="${customColor}"` : ''}>
                    <span class="port-number">${label}</span>
                    <span class="port-connector ${connectorClassBottom}">${createTriangleSvgWithStyle('bottom', group.direction, polygonStyle)}</span>
                </div>`;
            }
            html += `</div>`;
            html += `</div>`;
        }
    });
    
    return html;
}

/**
 * Categorize port groups by side based on template and instance layout
 * @param {Array} portGroups - Template's port groups
 * @param {Object} groupLayout - Instance's group layout overrides
 * @returns {Object} { leftGroups, rightGroups, topGroups, bottomGroups }
 */
export function categorizeGroupsBySide(portGroups, groupLayout = {}) {
    const leftGroups = [];
    const rightGroups = [];
    const topGroups = [];
    const bottomGroups = [];
    
    // Helper to get layout override (handles both index and legacy UUID keys)
    const getLayoutOverride = (groupIndex, group) => {
        // Try index key first (new format)
        if (groupLayout[groupIndex] !== undefined) {
            return groupLayout[groupIndex];
        }
        // Fall back to UUID key (legacy format)
        if (group.id && groupLayout[group.id] !== undefined) {
            return groupLayout[group.id];
        }
        return undefined;
    };

    portGroups.forEach((group, groupIndex) => {
        const override = getLayoutOverride(groupIndex, group);
        // Insert defaults to right (like outputs), since it sends signal out
        const defaultSide = group.direction === 'input' ? 'left' : 'right';
        const instanceSide = (typeof override === 'string') ? override : (override?.side || defaultSide);
        
        // Expand group if it has splitSize
        const expandedGroups = expandGroupForRendering(group, override, groupIndex);
        
        expandedGroups.forEach(g => {
            // Attach the group index for connection lookups
            g._groupIndex = groupIndex;
            
            if (instanceSide === 'left') {
                leftGroups.push(g);
            } else if (instanceSide === 'right') {
                rightGroups.push(g);
            } else if (instanceSide === 'top') {
                topGroups.push(g);
            } else if (instanceSide === 'bottom') {
                bottomGroups.push(g);
            }
        });
    });
    
    return { leftGroups, rightGroups, topGroups, bottomGroups };
}

/**
 * Generate the inner HTML for a device element
 * @param {Object} device - Device instance
 * @param {Object} template - Device template/definition
 * @param {Function} groupHasConnections - Function(deviceId, groupIndex, direction) => boolean
 * @param {number} instanceCount - Number of instances using this template
 * @returns {string} HTML string for device contents
 */
export function generateDeviceHtml(device, template, groupHasConnections, instanceCount = 1) {
    const groupLayout = device.groupLayout || {};
    const portOrder = device.portOrder || 'default'; // 'default' or 'clockwise'
    
    // Categorize groups by side
    const { leftGroups, rightGroups, topGroups, bottomGroups } = 
        categorizeGroupsBySide(template.portGroups, groupLayout);
    
    // Render each side (pass portOrder for clockwise ordering)
    const inputsHtml = renderLeftGroups(leftGroups, device.id, groupLayout, groupHasConnections, portOrder);
    const outputsHtml = renderRightGroups(rightGroups, device.id, groupLayout, groupHasConnections);
    const topHtml = renderTopGroups(topGroups, device.id, groupLayout, groupHasConnections);
    const bottomHtml = renderBottomGroups(bottomGroups, device.id, groupLayout, groupHasConnections, portOrder);
    
    // Use device.displayName if set, otherwise fall back to template.name
    const displayName = device.displayName || template.name;
    
    // Build header with manufacturer, name, version
    let headerHtml = '';
    if (template.manufacturer) {
        headerHtml += `<div class="device-header-manufacturer">${escapeHtml(template.manufacturer)}</div>`;
    }
    headerHtml += `<span class="device-header-name">${escapeHtml(displayName)}</span>`;
    if (template.version) {
        headerHtml += `<span class="device-header-version">${escapeHtml(template.version)}</span>`;
    }

    // Link indicator for multiple instances
    const linkIndicator = instanceCount > 1 
        ? `<span class="device-link-indicator" title="${instanceCount} linked instances">🔗 ${instanceCount}</span>` 
        : '';

    // Build device HTML with conditional top/bottom areas
    const hasTop = topGroups.length > 0;
    const hasBottom = bottomGroups.length > 0;

    return `
        ${linkIndicator}
        ${hasTop ? `<div class="device-top">${topHtml}</div>` : ''}
        <div class="device-body">
            <div class="device-inputs">${inputsHtml || '<div style="padding:8px;color:#666;font-size:0.8rem"></div>'}</div>
            <div class="device-header">${headerHtml}</div>
            <div class="device-outputs">${outputsHtml || '<div style="padding:8px;color:#666;font-size:0.8rem"></div>'}</div>
        </div>
        ${hasBottom ? `<div class="device-bottom">${bottomHtml}</div>` : ''}
    `;
}

/**
 * Create a device DOM element (without event bindings)
 * @param {Object} device - Device instance
 * @param {Object} template - Device template/definition
 * @param {Function} groupHasConnections - Function(deviceId, groupIndex, direction) => boolean
 * @param {number} instanceCount - Number of instances using this template
 * @returns {HTMLElement} Device DOM element ready for event binding
 */
export function createDeviceElement(device, template, groupHasConnections, instanceCount = 1) {
    const el = document.createElement('div');
    el.className = 'device';
    el.id = device.id;
    el.style.left = device.position.x + 'px';
    el.style.top = device.position.y + 'px';

    // Generate device HTML
    el.innerHTML = generateDeviceHtml(device, template, groupHasConnections, instanceCount);

    // Add internal cables SVG as first child (renders on top of background, below ports)
    const internalSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    internalSvg.classList.add('device-internal-cables');
    internalSvg.setAttribute('data-device-id', device.id);
    el.insertBefore(internalSvg, el.firstChild);

    return el;
}

// ============================================
// Standalone helper functions (used by app.js)
// ============================================

/**
 * Get the custom color for a group from device's groupLayout
 * @param {Object} device - Device instance
 * @param {string} groupId - Group ID or foldKey
 * @returns {string|null} Color or null
 */
export function getGroupColorFromDevice(device, groupId) {
    const layout = device.groupLayout?.[groupId];
    if (typeof layout === 'object' && layout.color) {
        return layout.color;
    }
    return null;
}

/**
 * Check if a group (or any of its subgroups) is folded in a device
 * @param {Object} device - Device instance
 * @param {string} groupId - Group ID
 * @param {number|null} portIndex - Optional port index for subgroup checking
 * @returns {boolean} True if folded
 */
export function isGroupFoldedInDevice(device, groupId, portIndex = null) {
    const layout = device.groupLayout;
    if (!layout) return false;
    
    // Check direct group fold (non-split groups)
    if (typeof layout[groupId] === 'object' && layout[groupId].folded) {
        return true;
    }
    
    // For subgroups, check if the specific subgroup containing portIndex is folded
    if (portIndex !== null) {
        const splitSize = typeof layout[groupId] === 'object' ? layout[groupId].splitSize : null;
        if (splitSize) {
            // Calculate which subgroup this port belongs to
            const subgroupOffset = Math.floor((portIndex - 1) / splitSize) * splitSize + 1;
            const subgroupKey = `${groupId}:${subgroupOffset}`;
            if (typeof layout[subgroupKey] === 'object' && layout[subgroupKey].folded) {
                return true;
            }
        }
    }
    
    // Check if any subgroup is folded (for bundling detection without specific port)
    for (const key of Object.keys(layout)) {
        if (key.startsWith(groupId + ':') && typeof layout[key] === 'object' && layout[key].folded) {
            return true;
        }
    }
    
    return false;
}

/**
 * Get the foldKey for a specific port in a group (handles subgroups)
 * The foldKey is based on the group's UUID (not numeric index), matching how fold state is stored
 * @param {Object} device - Device instance
 * @param {number} groupId - Group index
 * @param {number} portIndex - Port index
 * @param {Function} getTemplateForDevice - Function to get template for device
 * @returns {string} foldKey
 */
export function getFoldKeyForPort(device, groupId, portIndex, getTemplateForDevice) {
    // Get the group's UUID from the template
    const template = getTemplateForDevice(device);
    const group = template?.portGroups?.[groupId];
    const groupUUID = group?.id || String(groupId);
    
    const layout = device.groupLayout;
    if (!layout) return groupUUID;
    
    // Check for split using the UUID key
    const groupLayout = layout[groupUUID];
    const splitSize = typeof groupLayout === 'object' ? groupLayout.splitSize : null;
    
    if (!splitSize) {
        return groupUUID;
    }
    
    // Calculate which subgroup this port belongs to
    const subgroupOffset = Math.floor((portIndex - 1) / splitSize) * splitSize + 1;
    return `${groupUUID}:${subgroupOffset}`;
}

/**
 * Check if a specific port's subgroup is folded
 * @param {Object} device - Device instance
 * @param {number} groupId - Group index
 * @param {number} portIndex - Port index
 * @param {Function} getTemplateForDevice - Function to get template for device
 * @returns {boolean} True if folded
 */
export function isPortFolded(device, groupId, portIndex, getTemplateForDevice) {
    const layout = device.groupLayout;
    if (!layout) return false;
    
    const foldKey = getFoldKeyForPort(device, groupId, portIndex, getTemplateForDevice);
    return typeof layout[foldKey] === 'object' && layout[foldKey].folded;
}
